import { getHelpCenterUrl } from '@/helpers/getHelpCenterUrl';
import { cn } from '@/lib/cn';
import { Evidence } from '@heidi/design-system/icons';
import type { components } from '@heidi/schema/ml-scribe';
import { BookOpen, MessageSquareText } from 'lucide-react';
import { createElement, type ComponentType } from 'react';
import { type IntlShape, type MessageDescriptor } from 'react-intl';
import { trialRecapModalMessages } from './messages';

/**
 * Help-center article path linked from the trial recap modal description and the
 * clinician-trial-ended modal subtitle (plan rename / Scribe Plus / Scribe Team FAQ).
 */
export const PLAN_CHANGES_HELP_ARTICLE_PATH =
  'articles/13921980-heidi-plan-changes-names-features-faqs';

/** Localised help-center URL for the plan-changes FAQ (Scribe Plus / Scribe Team). */
export function getPlanChangesHelpArticleUrl(locale: string): string {
  return getHelpCenterUrl(PLAN_CHANGES_HELP_ARTICLE_PATH, locale);
}

export type EvidenceUsageStatsShape = components['schemas']['EvidenceUsageStatsResponse'];

export type TrialRecapFeatureKey =
  | 'evidenceAnswers'
  | 'answersInPatientSessions'
  | 'citationsSourced';

export interface TrialRecapFeatureUsage {
  key: TrialRecapFeatureKey;
  count: number;
}

export const EMPTY_TRIAL_RECAP_FEATURES: ReadonlyArray<TrialRecapFeatureUsage> = [
  { key: 'evidenceAnswers', count: 0 },
  { key: 'answersInPatientSessions', count: 0 },
  { key: 'citationsSourced', count: 0 },
];

/** Props passed to each row icon from {@link trial-recap-feature-list}. */
export type TrialRecapFeatureIconProps = {
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
};

/**
 * Evidence-product logo for the Evidence answers row. Matches the
 * `evidencelogo` instance in Figma node 10902:63323 / 11262:37335 ("2/3 used");
 * the design uses the Heidi Evidence product mark here rather than a generic
 * chat bubble.
 */
function TrialRecapEvidenceAnswersIcon(props: TrialRecapFeatureIconProps) {
  const { className, ...rest } = props;
  return createElement(Evidence, {
    size: 20,
    className: cn('shrink-0', className),
    ...rest,
  });
}

/** Optional info tooltip shown beside the row label (e.g. Evidence answers). */
export interface TrialRecapFeatureLabelTooltip {
  message: MessageDescriptor;
  triggerAriaLabel: MessageDescriptor;
}

export interface TrialRecapFeatureMeta {
  Icon: ComponentType<TrialRecapFeatureIconProps>;
  label: MessageDescriptor;
  /** When set, an info control next to the label opens a tooltip with `message`. */
  labelTooltip?: TrialRecapFeatureLabelTooltip;
}

/** How to render a usage count in a feature-row badge (e.g. "Used 34x", "3 sessions"). */
export type EvidenceUsageBadgeFormat = 'usedCount' | 'sessionCount' | 'citationCount';

export const TRIAL_RECAP_FEATURE_USAGE_BADGE_FORMAT: Record<
  TrialRecapFeatureKey,
  EvidenceUsageBadgeFormat
> = {
  evidenceAnswers: 'usedCount',
  answersInPatientSessions: 'sessionCount',
  citationsSourced: 'citationCount',
};

/**
 * Localised usage string for the right-hand badge on an Evidence usage feature row.
 */
export function formatEvidenceUsageBadge(
  intl: IntlShape,
  format: EvidenceUsageBadgeFormat,
  count: number
): string {
  switch (format) {
    case 'usedCount':
      return intl.formatMessage(trialRecapModalMessages.usageEvidenceAnswers, { count });
    case 'sessionCount':
      return intl.formatMessage(trialRecapModalMessages.usageAnswersInPatientSessions, {
        count,
      });
    case 'citationCount':
      if (count > 1000) {
        const rounded = Math.floor(count / 100) * 100;
        return intl.formatMessage(trialRecapModalMessages.usageCitationsSourcedCompact, {
          rounded,
        });
      }
      return intl.formatNumber(count, { useGrouping: false });
    default: {
      const _exhaustive: never = format;
      return _exhaustive;
    }
  }
}

/**
 * Icon and label for each row of the trial recap modal (all tiers and admin
 * modes share the same copy; counts come from per-user or team aggregate
 * {@link EvidenceUsageStatsShape}).
 */
export function getTrialRecapFeatureMeta(): Record<
  TrialRecapFeatureKey,
  TrialRecapFeatureMeta
> {
  return {
    evidenceAnswers: {
      Icon: TrialRecapEvidenceAnswersIcon,
      label: trialRecapModalMessages.featureLabelEvidenceAnswers,
      labelTooltip: {
        message: trialRecapModalMessages.evidenceAnswersTooltip,
        triggerAriaLabel: trialRecapModalMessages.evidenceAnswersTooltipAriaLabel,
      },
    },
    answersInPatientSessions: {
      // `MessageSquareText` (chat bubble) per Figma node 10902:63323 — also
      // matches the icon the end-of-trial modal uses for this same row in
      // `ClinicianTrialEndedFeatureList`, so the two surfaces stay visually
      // consistent.
      Icon: MessageSquareText,
      label: trialRecapModalMessages.featureLabelAnswersInPatientSessions,
    },
    citationsSourced: {
      Icon: BookOpen,
      label: trialRecapModalMessages.featureLabelCitationsSourced,
    },
  };
}

export function mapEvidenceUsageToTrialRecapFeatures(
  usageStats: EvidenceUsageStatsShape | null | undefined
): ReadonlyArray<TrialRecapFeatureUsage> {
  if (!usageStats) {
    return EMPTY_TRIAL_RECAP_FEATURES;
  }
  return [
    { key: 'evidenceAnswers', count: usageStats.answers_from_evidence },
    { key: 'answersInPatientSessions', count: usageStats.sessions_with_evidence_queries },
    { key: 'citationsSourced', count: usageStats.references_used },
  ];
}
